'use client'

import { NoSymbolIcon, TrashIcon, CurrencyRupeeIcon, SparklesIcon } from '@heroicons/react/24/outline'

const painPointsContent = {
  section: {
    eyebrow: "Why ScanMaadi?",
    title: "Restaurant Menus",
    titleHighlight: "Reimagined",
    subtitle: "We're solving the everyday frustrations that both restaurants and diners face with innovative digital solutions."
  },
  cards: [
    {
      id: 1,
      icon: NoSymbolIcon,
      iconBgColor: "bg-primary-500", // Changed to specific color class
      gradientFrom: "from-primary-500",
      gradientTo: "to-primary-600",
      title: "Frustrated Customers",
      description: "Nobody enjoys squinting at tiny PDF menus or blurry photos. Your customers deserve clarity and ease.",
      solution: "Our solution: Crystal-clear, mobile-friendly digital menus",
      solutionColor: "text-primary-600"
    },
    {
      id: 2,
      icon: TrashIcon,
      iconBgColor: "bg-accent-500",
      gradientFrom: "from-accent-500",
      gradientTo: "to-accent-600",
      title: "Damaged Menu Cards",
      description: "Physical menus quickly become stained, torn, and outdated — creating a poor impression and hygiene concerns.",
      solution: "Our solution: Always pristine, up-to-date digital experience",
      solutionColor: "text-accent-600"
    },
    {
      id: 3,
      icon: CurrencyRupeeIcon,
      iconBgColor: "bg-highlight-500", // Changed
      gradientFrom: "from-highlight-500", // Changed
      gradientTo: "to-highlight-600",   // Changed
      title: "Costly Reprints",
      description: "Every price change or new dish means reprinting your entire menu — wasting money, time, and resources.",
      solution: "Our solution: Update prices and items instantly, for free",
      solutionColor: "text-highlight-600"
    }
  ],
  cta: {
    text: "ScanMaadi: The Smart Solution for Modern Restaurants!",
  }
};

export default function PainPointsSection() {
  return (
    <div id="why-scanmaadi" className="py-28 sm:py-36 bg-gradient-to-b from-background-100 via-white to-background-50 relative overflow-hidden">
      {/* Decorative Blobs */}
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-primary-100/50 rounded-full blur-3xl opacity-60 animate-pulse"></div>
      <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-accent-100/50 rounded-full blur-3xl opacity-60 animate-pulse animation-delay-2000"></div>
      
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mb-20 text-center">
          <h2 className="text-lg font-semibold text-primary-500 tracking-wider uppercase">
            {painPointsContent.section.eyebrow}
          </h2>
          <p className="mt-3 text-4xl font-extrabold tracking-tight text-text-700 sm:text-5xl lg:text-6xl">
            {painPointsContent.section.title} <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-highlight-400">{painPointsContent.section.titleHighlight}</span>
          </p>
          <p className="mt-6 max-w-3xl text-xl text-text-500 mx-auto leading-relaxed">
            {painPointsContent.section.subtitle}
          </p>
        </div>

        <div className="relative">
          {/* Optional: Subtle dashed line connecting visually if cards were perfectly horizontal, not needed with current layout much */}
          {/* <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px border-t-2 border-dashed border-primary-200/70" aria-hidden="true"></div> */}

          <div className="grid grid-cols-1 gap-10 md:gap-8 lg:grid-cols-3 relative z-10">
            {painPointsContent.cards.map((card, index) => (
              <div 
                key={card.id} 
                className="relative group flex flex-col"
                style={{ animationDelay: `${index * 150}ms` }} // Stagger animation if using one
              >
                <div className={`absolute -inset-1.5 bg-gradient-to-br ${card.gradientFrom} ${card.gradientTo} rounded-xl blur-md opacity-30 group-hover:opacity-60 transition-all duration-500 group-hover:duration-300 animate-hue-shift animation-delay-${index * 1000}`}></div>
                <div className="relative bg-white rounded-xl shadow-xl p-8 ring-1 ring-black/5 flex flex-col h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl interactive-transition">
                  <div className="flex justify-start mb-6">
                    <div className={`flex items-center justify-center h-14 w-14 rounded-lg ${card.iconBgColor} text-white shadow-lg group-hover:scale-110 interactive-transition`}>
                      <card.icon className="h-8 w-8" aria-hidden="true" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-text-700 mb-3 group-hover:text-primary-600 interactive-transition">{card.title}</h3>
                  <p className="text-text-500 mb-5 leading-relaxed text-pretty">
                    "{card.description}"
                  </p>
                  <div className="mt-auto pt-5 border-t border-background-200/80">
                    <p className={`text-md font-semibold ${card.solutionColor} flex items-center`}>
                       <SparklesIcon className="h-5 w-5 mr-2 opacity-70" />
                      {card.solution}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-24 text-center relative z-10">
            <div className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-primary-600 via-primary-500 to-accent-500 shadow-xl transform transition-all duration-500 hover:scale-105 hover:shadow-2xl">
               <p className="text-xl font-bold text-white">
                {painPointsContent.cta.text}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Subtle decorative shape for transition to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background-50 to-transparent z-0 opacity-70" style={{clipPath: 'ellipse(100% 50% at 50% 100%)'}}></div>
    </div>
  )
}